// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';
import { getErrorMessage } from '@/utils/errors';
import { RootState } from '../index';
// TEMPORARY, dev-only coupling to the brand-mailbox mock layer (see the note in
// resolveMainWalletForCurrency). ONE neutral leaf module, never a page folder, so either mailbox
// scenario can be deleted whole without touching this slice; delete the import with that branch.
import { WALLET_OVERRIDE_ACTIVE, MAILBOX_LEDGER_CURRENCY, loadMailboxSpendTotal } from '@/nova/mock-data/mailbox-wallet';

const DEBUG = false;
const apiUrl = window.config.userApiUrl + '/wallet';

/** `/wallet/list` is paginated; the server caps `limit` at 50 (and accepts `offset`). */
const WALLET_LIST_PAGE = 50;

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getCurrentCurrency = () => {
  const currency = localStorage.getItem('currentCurrency');
  if (currency) return currency;
  return window.config.defaultCurrency;
};

/** Balances/amounts arrive from the API as strings ("500"); coerce to a finite number. */
const toNumber = (value: unknown): number => {
  const n = parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

export type CurrencyType = 'crypto' | 'fiat';

export type CurrencySettings = {
  code: string;
  decimals?: number;
  name?: string;
  image?: string;
  subdivisions?: number;
  type?: CurrencyType;
  exchangeRate?: number; // to USD
};

/* ---------- API response shapes (see src/store/samples/wallet/curls) ---------- */

// `AI_CREDITS` is the owner-level virtual-currency AI-credits wallet (currency `AIC`, 1 credit = 1 AIC;
// balance is a credit COUNT, not money — see nova/utils/credits.ts). `state.credits` is null ONLY until
// the wallet list first loads; thereafter it is the real balance (0 when the owner has no AI_CREDITS row
// yet — the BE creates the wallet on first purchase).
export type WalletType =
  | 'MAIN'
  | 'ACQUISITION'
  | 'DAILY_ACQUISITION'
  | 'WITHDRAWAL'
  | 'AI_CREDITS'
  // The platform's invoice-holding wallet: MAIN funds move into it when an invoice is paid. Its ledger
  // leg is folded out of the activity feed (redundant with the invoice + the deposit it funds).
  | 'INVOICE_PAYMENTS';

/** A raw wallet record as returned by `/wallet/list`. */
export interface RawWallet {
  id: string;
  user_id: string;
  wallet_type: WalletType;
  balance: string;
  currency: string;
  /**
   * Which casino instance (brand) the wallet belongs to. `ACQUISITION` /
   * `DAILY_ACQUISITION` carry the brand's id; `MAIN` is owner-level, so it holds
   * the all-zeros sentinel (`00000000-…`). See `docs/api.owners-hub.wallet.md`.
   */
  casino_instance_id: string;
  allow_negative_balance: boolean;
  updates: number;
  details: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

/** `/wallet/balance` */
export interface WalletBalanceResponse {
  balance: string;
  currency: string;
}

/** `/wallet/list` */
export interface WalletListResponse {
  wallets: {
    data: RawWallet[];
    total: number;
  };
}

export type OperationType = 'deposit' | 'transfer' | 'withdrawal' | 'TOPUP' | string;
export type OperationStatus = 'completed' | 'failed' | 'pending' | string;

/** A row from `/wallet/operations`. */
export interface WalletOperation {
  id: string;
  operation_key: string;
  operation_type: OperationType;
  source_user_id: string;
  source_wallet_id: string;
  source_wallet_type: WalletType;
  destination_user_id: string;
  destination_wallet_id: string;
  destination_wallet_type: WalletType;
  amount: string;
  currency: string;
  details: Record<string, any> | null;
  status: OperationStatus;
  log: { stack?: string; message?: string } | null;
  /**
   * The casino instance (brand) on each side of the movement. A budget top-up
   * funds a brand's `ACQUISITION` wallet, so its `destination_casino_instance_id`
   * is the brand; the owner-level `MAIN` side is the all-zeros sentinel. Used to
   * resolve which casino each top-up belongs to. See `docs/api.owners-hub.wallet.md`.
   */
  source_casino_instance_id?: string;
  destination_casino_instance_id?: string;
  created_at: string;
}

export type TransactionType = 'credit' | 'debit' | string;

/** A row from `/wallet/transactions`. */
export interface WalletTransaction {
  id: string;
  user_id: string;
  transaction_type: TransactionType;
  wallet_id: string;
  operation_id: string;
  requested_amount: string;
  requested_currency: string;
  amount: string;
  currency: string;
  exchange_rate: string;
  wallet_balance_after: string;
  details: Record<string, any> | null;
  created_at: string;
}

/** `/wallet/operations` */
export interface WalletOperationsResponse {
  data: WalletOperation[];
  total: number;
}

/** `/wallet/transactions` */
export interface WalletTransactionsResponse {
  data: WalletTransaction[];
  total: number;
}

/* ---------- withdrawal requests ----------

A withdrawal request is the owner asking to take money OUT of their MAIN wallet. On
create, the backend parks the amount in a dedicated `WITHDRAWAL` wallet (a `MAIN →
WITHDRAWAL` operation) and opens a request that a back-office app later approves /
rejects. So one logical withdrawal shows up across THREE feeds: the request row here,
a `WITHDRAWAL_*` operation, and its ledger legs, which is why the activity feed treats
this API as the source of truth and suppresses any operation touching the `WITHDRAWAL`
wallet (see `pages/wallet/index.tsx`). See `docs/api.owners-hub.wallet.md`. */

/** `pending` is open (awaiting the back office); everything else is resolved. The exact
   resolved vocabulary is owned by the back office, so keep this open so a new status still
   renders (it falls into the "resolved" bucket and the activity feed). */
export type WithdrawalRequestStatus = 'pending' | 'approved' | 'completed' | 'rejected' | string;

/** A row from `/wallet/withdrawal-requests`. `amount` arrives as a string. `details.reason`
   carries the back office's rejection note; `approved` flips once a decision is made. */
export interface WithdrawalRequest {
  id: string;
  user_id: string;
  amount: string;
  currency: string;
  status: WithdrawalRequestStatus;
  approved: boolean | null;
  details: { reason?: string } | Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** A single `{ name, op, value }` filter clause for the withdrawal-requests query. */
export interface WithdrawalRequestFilter {
  name: string;
  op: 'eq' | 'ne' | string;
  value: string;
}

/** `/wallet/withdrawal-requests` (the paginated, filterable list). */
export interface WithdrawalRequestsResponse {
  data: WithdrawalRequest[];
  total: number;
}

/* ---------- invoices ----------

Invoices for the owner: the configurator brand-creation purchases (`type: "casino_create"`)
plus wallet deposits (`type: "deposit"`), both via Stripe. `fetchInvoices` fetches the FULL
list; the wallet view splits it by `status` into Paid (settled) and Pending (awaiting payment,
incl. abandoned deposit intents) tabs. Lives under `{userApiUrl}/invoice/list` (NOT under
`/wallet`), same auth + `{ limit, offset }` paging convention as the lists above. The owner
is resolved server-side from the token, so no id is sent. `price` arrives as a string. The
full configurator-session snapshot the invoice paid for rides along in `invoice_data`. See
the `invoice-list-api` note for the probed contract. */

/** Server-owned vocabulary; keep open so a new status still renders. `paid` is settled. */
export type InvoiceStatus = 'paid' | 'pending' | 'unpaid' | 'failed' | 'cancelled' | string;

/** One entry of an invoice's payment trail (`payment_data[]`). */
export interface InvoicePaymentEntry {
  payment_id: string;
  status: string;
  date: string;
}

/** The feature a product activation / upgrade invoice pays for (`invoice_data.campaign_item`).
 *  Its `id` is the CATALOG item id — the same numeric id `/configurator` `gamification_items[].id`
 *  and the Product pages join on — so a pending invoice can be matched to the feature it belongs
 *  to. (`feature_id` is a different, downstream id and is NOT the catalog id.) */
export interface InvoiceCampaignItem {
  id: number;
  name?: string;
  type?: string;
  feature_id?: number;
}

/** The tier a product activation / upgrade invoice activates (`invoice_data.new_item`): the
 *  target CATALOG `tier_id` (+ its price) and the instance-scoped record UUID the purchase lands
 *  on. Lets the Product-detail page show which tier the pending payment is for. */
export interface InvoiceNewItem {
  id: string;
  type?: string;
  item_id: number;
  tier_id: number;
  base_price?: number;
  discounted_price?: number;
}

/** A row from `POST {userApiUrl}/invoice/list`. `price` is a string ("500" / "759.92"). */
export interface Invoice {
  id: string;
  user_id: string;
  /** What the invoice is for, e.g. `casino_create` (configurator brand creation) or
   *  `casino_upgrade` (a Product feature activation / tier change). */
  type: string;
  session_id: string | null;
  casino_instance_id: string | null;
  status: InvoiceStatus;
  price: string;
  currency: string;
  due_date: string;
  created_at: string;
  updated_at: string;
  payment_id: string | null;
  payment_data: InvoicePaymentEntry[] | null;
  /** The snapshot this invoice paid for. For a brand-creation invoice this is the configurator
   *  session (opaque ids + theme tokens); for a Product feature invoice (`casino_upgrade`) it
   *  carries `campaign_item` (the feature) + `new_item` (the target tier) instead. */
  invoice_data: {
    session_id?: string;
    session_data?: { configuration_data?: Record<string, unknown> } & Record<string, unknown>;
    /** Present only on Product feature invoices: the feature (`campaign_item`, keyed by CATALOG
     *  item id) and the tier (`new_item`) the invoice activates. Absent on brand-create/deposit. */
    campaign_item?: InvoiceCampaignItem;
    new_item?: InvoiceNewItem;
  } & Record<string, unknown> | null;
}

/** `POST {userApiUrl}/invoice/list` */
export interface InvoiceListResponse {
  data: Invoice[];
  total: number;
}

/**
 * Index a brand's OPEN Product-feature invoices by the CATALOG item id they pay for, so a
 * Product page can ask "does this feature have an unpaid invoice?" in O(1) and swap its
 * Activate CTA for a Finish-payment one.
 *
 * A feature invoice (`type: "casino_upgrade"`) carries `invoice_data.campaign_item` (the feature)
 * and `invoice_data.new_item` (the target tier). We keep only:
 *   - `status === 'pending'` (still payable);
 *   - `casino_instance_id === casinoInstanceId` — invoices are OWNER-scoped, so a pending invoice
 *     from another brand must never leak onto this one;
 *   - rows carrying BOTH a numeric `campaign_item.id` AND a numeric `new_item.tier_id` — i.e. a
 *     feature + its target tier. This positively identifies a feature-activation invoice rather
 *     than relying on the ABSENCE of `campaign_item` on other rows: a brand-create invoice carries
 *     `session_data` (no `new_item`) and a deposit carries neither, so both drop out here even if a
 *     future backend attached a stray `campaign_item` to them.
 * When more than one is open for the same feature, the newest (`created_at`) wins. Returns an
 * empty map when the brand id is unknown (nothing to scope to).
 */
export const buildPendingFeatureInvoiceMap = (
  invoices: Invoice[],
  casinoInstanceId: string | undefined | null,
): Map<number, Invoice> => {
  const map = new Map<number, Invoice>();
  if (!casinoInstanceId) return map;
  for (const invoice of invoices) {
    if (invoice.status !== 'pending') continue;
    if (invoice.casino_instance_id !== casinoInstanceId) continue;
    const itemId = invoice.invoice_data?.campaign_item?.id;
    const tierId = invoice.invoice_data?.new_item?.tier_id;
    // Both must be present + numeric: a feature invoice always names the feature AND the tier.
    if (typeof itemId !== 'number' || typeof tierId !== 'number') continue;
    const existing = map.get(itemId);
    if (!existing || invoice.created_at > existing.created_at) {
      map.set(itemId, invoice);
    }
  }
  return map;
};

/* ---------- slice state ---------- */

export interface WalletState {
  /** Per-type balances derived from `/wallet/list`. */
  main: number;
  acquisition: number;
  total: number;
  currency: string;
  /** The owner-level AI_CREDITS wallet balance — a credit COUNT, not money, so it is NOT part of
   *  `total`. `null` ONLY until the wallet list first loads (consumers show a skeleton then); once
   *  loaded it is the real balance, which is 0 when the owner has no AI_CREDITS row yet (the BE creates
   *  the wallet on first purchase) — the chip shows a real 0, not a skeleton. */
  credits: number | null;
  /**
   * The last `/wallet/list` read failed, so `credits` is the balance of an earlier read and may be stale (AI credits
   * are also spent on the services, e.g. by blog Autopilot). Cleared only by a read that succeeds: unlike `error`, a
   * read merely starting does not make the old balance trustworthy again.
   */
  creditsReadFailed: boolean;

  /** Raw wallet records from `/wallet/list`. */
  wallets: RawWallet[];

  /** Aggregate balance from `/wallet/balance`. */
  balance: number;

  /** `/wallet/operations`. */
  operations: WalletOperation[];
  operationsTotal: number;

  /** `/wallet/transactions`. */
  transactions: WalletTransaction[];
  transactionsTotal: number;

  /** `/wallet/list` request status. */
  loading: boolean;
  loaded: boolean;
  error: string | null | undefined;

  /** `/wallet/balance` request status. */
  balanceLoading: boolean;
  balanceLoaded: boolean;
  balanceError: string | null | undefined;

  /** `/wallet/operations` request status. */
  operationsLoading: boolean;
  operationsLoaded: boolean;
  operationsError: string | null | undefined;

  /** `/wallet/transactions` request status. */
  transactionsLoading: boolean;
  transactionsLoaded: boolean;
  transactionsError: string | null | undefined;

  /** Open (`status === pending`) withdrawal requests: the "Pending withdrawals" card. */
  pendingWithdrawals: WithdrawalRequest[];
  pendingWithdrawalsLoading: boolean;
  pendingWithdrawalsLoaded: boolean;
  pendingWithdrawalsError: string | null | undefined;

  /** Resolved (`status !== pending`) withdrawal requests, merged into recent activity. */
  resolvedWithdrawals: WithdrawalRequest[];
  resolvedWithdrawalsLoading: boolean;
  resolvedWithdrawalsLoaded: boolean;
  resolvedWithdrawalsError: string | null | undefined;

  /** `POST /wallet/withdrawal-request` (creating a new payout request) status. */
  withdrawalSubmitting: boolean;
  withdrawalSubmitError: string | null | undefined;

  /** Owner invoices from `POST {userApiUrl}/invoice/list`, newest-first when rendered. */
  invoices: Invoice[];
  invoicesTotal: number;
  invoicesLoading: boolean;
  invoicesLoaded: boolean;
  invoicesError: string | null | undefined;

  /** `POST /wallet/pay-invoice`: the invoice id currently being settled from MAIN
   *  (drives per-row spinners / disabled Pay buttons), or null when idle. */
  payingInvoiceId: string | null;
  payInvoiceError: string | null | undefined;

  /** `POST {userApiUrl}/invoice/cancel`: the invoice id currently being cancelled
   *  (drives the confirm dialog's busy state), or null when idle. */
  cancellingInvoiceId: string | null;
  cancelInvoiceError: string | null | undefined;
}

/* ---------- MAIN-wallet resolution (currency-matched source of funds) ---------- */

/** The MAIN-wallet view to spend against a charge priced in `currency`. An owner can hold more
 *  than one MAIN wallet (one per currency), so a flow that shows a balance next to a price must
 *  pick the MAIN wallet in the SAME currency as the price — otherwise the figure and the amount
 *  charged read in different currencies. */
export interface MainWalletView {
  /** Available MAIN balance (major units, clamped >= 0), or null until `/wallet/list` has loaded
   *  (callers keep a skeleton rather than flash a misleading 0). */
  balance: number | null;
  /** ISO-4217 code the returned `balance` is actually denominated in. */
  currency: string;
  /** True when a MAIN wallet in the requested `currency` was found; false = the primary fallback. */
  matched: boolean;
}

/**
 * The mailbox ledgers, off a MAIN balance, in whole cents so the header never renders a
 * floating-point tail. Only in the ledgers' own currency: they are USD spend, and taking them off
 * an EUR balance would be wrong money. Floors at zero. See the note on the resolver below.
 */
const debitMailboxLedger = (balance: number, currency: string): number => {
  if (!WALLET_OVERRIDE_ACTIVE || (currency ?? '').toUpperCase() !== MAILBOX_LEDGER_CURRENCY) return balance;
  const spentCents = Math.round(loadMailboxSpendTotal() * 100);
  if (spentCents <= 0) return balance;
  return Math.max(0, Math.round(balance * 100) - spentCents) / 100;
};

/**
 * Resolve the MAIN wallet the owner should pay a `currency`-priced charge from. Prefers the MAIN
 * wallet whose currency matches `currency` (the currently-selected casino's billing currency) so
 * the balance shown lines up with the price; when the owner has no MAIN wallet in that currency,
 * falls back to the PRIMARY MAIN wallet (the aggregate the slice derives into `main`), reported in
 * its own currency. Not a redux selector, so callers can hold the whole `ownerWallet` object
 * — a stable reference — and derive this in render without a new-object-per-dispatch churn.
 *
 * DEV-ONLY, TEMPORARY: outside production the balance (matched, or the primary fallback) also has
 * the brand-mailbox spend ledgers subtracted on read. A mailbox purchase never reaches the network yet (no endpoint mints
 * a mailbox invoice), so the server's balance does not move and the only record of that spend is
 * a per-brand ledger each mailbox scenario keeps under its own localStorage key. Applying the SUM
 * of those ledgers HERE, in the one funnel every balance in the app reads through (the top bar,
 * the Wallet page, the product sheets, both mailbox sections), keeps them all on one figure;
 * applying it to slice state would be wiped by the next `fetchOwnerWallets`. The keys and the
 * summed read live in the neutral `nova/mock-data/mailbox-wallet.ts`, never in a page folder, so
 * either scenario can be deleted whole without touching this slice. This is a storage read and a
 * nova import inside an otherwise pure function, deliberately. THE WHOLE BRANCH COMES OUT when a
 * real `payInvoiceFromWallet` settles a real mailbox invoice: the server's own balance then moves,
 * and this function, the import above and `mailbox-wallet.ts` go together.
 */
export const resolveMainWalletForCurrency = (
  wallet: WalletState | null | undefined,
  currency: string | null | undefined,
): MainWalletView => {
  const target = (currency ?? '').trim().toUpperCase();
  if (!wallet || !wallet.loaded) {
    return { balance: null, currency: target || wallet?.currency || getCurrentCurrency(), matched: false };
  }
  const match = target
    ? wallet.wallets.find((w) => w.wallet_type === 'MAIN' && (w.currency ?? '').toUpperCase() === target)
    : undefined;
  if (match) {
    return { balance: debitMailboxLedger(Math.max(0, toNumber(match.balance)), match.currency), currency: match.currency, matched: true };
  }
  // No MAIN wallet in the requested currency — fall back to the primary MAIN wallet (the slice's
  // derived aggregate), in its own currency.
  // Debited like a match: when that primary wallet is the USD one, it is the same wallet the
  // mailbox sheets match, and the header must show the same figure.
  return { balance: debitMailboxLedger(Math.max(0, wallet.main), wallet.currency), currency: wallet.currency, matched: false };
};

/* ---------- thunks ----------

Endpoints (see src/store/samples/wallet/curls):
  GET  {userApiUrl}/wallet/balance
  POST {userApiUrl}/wallet/list
  POST {userApiUrl}/wallet/operations
  POST {userApiUrl}/wallet/transactions
All require an `Authorization` header carrying the access token.
*/

export const fetchWalletBalance = createAsyncThunk<WalletBalanceResponse, void, GenericError>(
  'wallet/balance',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.get(`${apiUrl}/balance`, { headers });
      DEBUG && console.log('[Wallet Slice] balance response', response);

      if (!isNil(response.data)) return response.data;
      return rejectWithValue({ error: "Couldn't fetch wallet balance" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

export const fetchOwnerWallets = createAsyncThunk<WalletListResponse, void, GenericError>(
  'wallet/list',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) {
        DEBUG && console.log('[Wallet Slice] fetchOwnerWallets list rejected - no auth token');
        return rejectWithValue({ error: 'No authentication token found' });
      }

      // `/wallet/list` is paginated and the API exposes no wallet_type filter (only
      // `limit` <= 50 and `offset`), so page through with a computed offset at the
      // server-max page size until every wallet is collected. Otherwise the owner-level
      // MAIN wallet — which sorts after the brand (ACQUISITION/DAILY) wallets — is dropped
      // past the first page, leaving `main` at 0 and the headline balance showing $0.
      const all: RawWallet[] = [];
      let total = 0;
      for (let offset = 0; ; offset += WALLET_LIST_PAGE) {
        const response = await axios.post(
          `${apiUrl}/list`,
          { limit: WALLET_LIST_PAGE, offset },
          { headers },
        );
        DEBUG && console.log('[Wallet Slice] list page', { offset, response });
        const page = response.data?.wallets;
        const data: RawWallet[] = Array.isArray(page?.data) ? page.data : [];
        total = typeof page?.total === 'number' ? page.total : all.length + data.length;
        all.push(...data);
        if (data.length < WALLET_LIST_PAGE || all.length >= total) break;
      }

      return { wallets: { data: all, total } };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

export const fetchWalletOperations = createAsyncThunk<WalletOperationsResponse, void, GenericError>(
  'wallet/operations',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // `/wallet/operations` is paginated like `/wallet/list`: when no `limit` is sent the
      // server returns only a SMALL first page (~10) of what can be dozens of movements, so an
      // empty body silently truncates the activity feed. Page through at the server-max size so
      // the feed sees the whole ledger (otherwise most top-ups/withdrawals never render).
      const all: WalletOperation[] = [];
      let total = 0;
      for (let offset = 0; ; offset += WALLET_LIST_PAGE) {
        const response = await axios.post(`${apiUrl}/operations`, { limit: WALLET_LIST_PAGE, offset }, { headers });
        DEBUG && console.log('[Wallet Slice] operations page', { offset, response });
        const data: WalletOperation[] = Array.isArray(response.data?.data) ? response.data.data : [];
        total = typeof response.data?.total === 'number' ? response.data.total : all.length + data.length;
        all.push(...data);
        if (data.length < WALLET_LIST_PAGE || all.length >= total) break;
      }
      return { data: all, total };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

export const fetchWalletTransactions = createAsyncThunk<WalletTransactionsResponse, void, GenericError>(
  'wallet/transactions',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // Paginated exactly like `/wallet/operations` above: an empty body returns only the first
      // ~10 of (here) many ledger legs, which is what hid the deposit credits (lone credits into
      // MAIN, with no operation) from the feed. Page through at the server-max size for a
      // complete ledger so every deposit / top-up / payout leg is available to the feed.
      const all: WalletTransaction[] = [];
      let total = 0;
      for (let offset = 0; ; offset += WALLET_LIST_PAGE) {
        const response = await axios.post(`${apiUrl}/transactions`, { limit: WALLET_LIST_PAGE, offset }, { headers });
        DEBUG && console.log('[Wallet Slice] transactions page', { offset, response });
        const data: WalletTransaction[] = Array.isArray(response.data?.data) ? response.data.data : [];
        total = typeof response.data?.total === 'number' ? response.data.total : all.length + data.length;
        all.push(...data);
        if (data.length < WALLET_LIST_PAGE || all.length >= total) break;
      }
      return { data: all, total };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Shared body builder for the `/wallet/withdrawal-requests` list query. */
const withdrawalRequestsBody = (filter?: WithdrawalRequestFilter) => ({
  offset: 0,
  limit: 50,
  ...(filter ? { filter: [filter] } : {}),
});

/** Open requests (awaiting the back office): feeds the "Pending withdrawals" card. */
export const fetchPendingWithdrawalRequests = createAsyncThunk<WithdrawalRequestsResponse, void, GenericError>(
  'wallet/withdrawalRequests/pending',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.post(
        `${apiUrl}/withdrawal-requests`,
        withdrawalRequestsBody({ name: 'status', op: 'eq', value: 'pending' }),
        { headers },
      );
      DEBUG && console.log('[Wallet Slice] pending withdrawal-requests response', response);

      if (!isNil(response.data)) return response.data;
      return rejectWithValue({ error: "Couldn't fetch withdrawal requests" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Resolved requests (approved / completed / rejected / …), merged into recent activity. */
export const fetchResolvedWithdrawalRequests = createAsyncThunk<WithdrawalRequestsResponse, void, GenericError>(
  'wallet/withdrawalRequests/resolved',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.post(
        `${apiUrl}/withdrawal-requests`,
        withdrawalRequestsBody({ name: 'status', op: 'ne', value: 'pending' }),
        { headers },
      );
      DEBUG && console.log('[Wallet Slice] resolved withdrawal-requests response', response);

      if (!isNil(response.data)) return response.data;
      return rejectWithValue({ error: "Couldn't fetch withdrawal requests" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Create a payout request: parks `amount` of `currency` from MAIN into the WITHDRAWAL
   wallet and opens a `pending` request for the back office. `payment_method` tells the back
   office which rail to settle on (`bank` transfer vs `crypto` wallet); the account details
   for that rail are still resolved server-side from billing. */
export const createWithdrawalRequest = createAsyncThunk<
  WithdrawalRequest | null,
  { amount: number; currency: string; payment_method: 'bank' | 'crypto' },
  GenericError
>(
  'wallet/withdrawalRequests/create',
  async ({ amount, currency, payment_method }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.post(
        `${apiUrl}/withdrawal-request`,
        { amount, currency, payment_method },
        { headers },
      );
      DEBUG && console.log('[Wallet Slice] createWithdrawalRequest response', response);

      // The caller refetches the lists/balances; the created row is returned for convenience.
      return (response.data ?? null) as WithdrawalRequest | null;
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- deposit (wallet top-up via Stripe) ----------

Reuses the configurator's Stripe integration (see `nova/pages/configurator-v3/data/payments.ts`):
the backend creates a Stripe PaymentIntent and the browser confirms its client secret. The call is
`POST {userApiUrl}/invoice/deposit { amount, currency }` (note: under `/invoice`, NOT `/wallet`) and
it is the wallet sibling of the configurator's `POST {sessionApiUrl}/invoice { session_id }`: same
response shape (the PaymentIntent client secret arrives as `provider_reference`), but the amount is
passed explicitly rather than derived from a saved session. It returns the created invoice
(`type: "deposit"`, `status: "pending"`).

Each call mints a FRESH pending deposit invoice (even for abandoned attempts), so these accumulate
as `type: "deposit"` rows in `/invoice/list`. `fetchInvoices` filters them out server-side so the
Invoices tab stays platform billing. See `docs/api.owners-hub.wallet.md`. */

/** Create a Stripe PaymentIntent for a wallet top-up of `amount` `currency`, returning its client
   secret (`provider_reference`) for in-browser confirmation. POSTs `{userApiUrl}/invoice/deposit`. */
export const createDepositIntent = createAsyncThunk<
  { clientSecret: string },
  { amount: number; currency: string },
  GenericError
>('wallet/deposit/createIntent', async ({ amount, currency }, { rejectWithValue, getState }) => {
  try {
    const headers = getAuthHeaders(getState() as RootState);
    if (!headers) return rejectWithValue({ error: 'No authentication token found' });

    const response = await axios.post(
      `${window.config.userApiUrl}/invoice/deposit`,
      { amount, currency },
      { headers },
    );
    DEBUG && console.log('[Wallet Slice] createDepositIntent response', response);

    const data = response.data as { provider_reference?: string; client_secret?: string } | undefined;
    const clientSecret = data?.provider_reference ?? data?.client_secret;
    if (!clientSecret) return rejectWithValue({ error: 'deposit returned no client secret' });
    return { clientSecret };
  } catch (error: any) {
    return rejectWithValue({ error: getErrorMessage(error) });
  }
});

/** `/invoice/list` is paginated the same way `/wallet/list` is (server caps `limit`). */
const INVOICE_LIST_PAGE = 50;

/** Fetch every owner invoice (paging through `/invoice/list`). Sorting is left to the view. */
export const fetchInvoices = createAsyncThunk<InvoiceListResponse, void, GenericError>(
  'wallet/invoices',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // The invoice list is owner-scoped (user_id from the token) and lives at the user-api
      // root, NOT under `/wallet`. Page through at the server-max size so a long history is
      // still complete. `limit`/`offset` MUST be ints (a string leaks a Prisma 500).
      //
      // The wallet splits invoices across two tabs (Paid / Pending), so fetch the FULL list
      // (no status filter) and let the view group by `status`. Page through at the server-max
      // size so a long history is complete. `limit`/`offset` MUST be ints (a string leaks a
      // Prisma 500). Live shape (25 Jun 2026): 42 invoices = 16 casino_create + 26 deposit, 17
      // paid vs 25 pending (every POST /invoice/deposit mints a fresh pending intent, so the
      // Pending tab also carries abandoned top-up attempts). The server also accepts a
      // `filter:[{name:'status',op:'eq',value}]` if these ever need fetching independently; one
      // unfiltered fetch + client split is simpler while the list is small.
      const invoiceUrl = `${window.config.userApiUrl}/invoice/list`;
      const all: Invoice[] = [];
      let total = 0;
      for (let offset = 0; ; offset += INVOICE_LIST_PAGE) {
        const response = await axios.post(invoiceUrl, { limit: INVOICE_LIST_PAGE, offset }, { headers });
        DEBUG && console.log('[Wallet Slice] invoice/list page', { offset, response });
        const data: Invoice[] = Array.isArray(response.data?.data) ? response.data.data : [];
        total = typeof response.data?.total === 'number' ? response.data.total : all.length + data.length;
        all.push(...data);
        if (data.length < INVOICE_LIST_PAGE || all.length >= total) break;
      }
      return { data: all, total };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- pay an invoice from the MAIN wallet ----------

`POST {userApiUrl}/wallet/pay-invoice { invoice_id }` settles an existing owner invoice
from the MAIN wallet balance — the in-app alternative to paying it with Stripe. It backs
the "pay from wallet" path: paying a pending platform invoice (e.g. a recurring casino
subscription invoice, or an invoice minted when a gamification feature is activated)
without leaving for a card checkout. The owner is resolved server-side from the token, so
only the `invoice_id` is sent. On success the caller refetches the balance + invoices so
the paid state and the new balance render. See `docs/api.owners-hub.product-activation.md`. */

/** Settle an existing invoice from the MAIN wallet. Returns the `invoice_id` for the
   caller to refetch around; throws (rejected) on an unknown/already-paid invoice. */
export const payInvoiceFromWallet = createAsyncThunk<{ invoice_id: string }, { invoice_id: string }, GenericError>(
  'wallet/payInvoice',
  async ({ invoice_id }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      await axios.post(`${apiUrl}/pay-invoice`, { invoice_id }, { headers });
      DEBUG && console.log('[Wallet Slice] payInvoiceFromWallet settled', invoice_id);
      return { invoice_id };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- cancel a pending invoice ----------

`POST {userApiUrl}/invoice/cancel { invoice_id }` voids a still-open owner invoice — a
`pending` platform charge the owner decides not to pay (e.g. a "Casino upgrade" invoice, or
a recurring subscription invoice). The owner is resolved server-side from the token, so only
the `invoice_id` is sent. It is terminal for that invoice: the record moves to `cancelled`
and can no longer be paid, so the row drops out of the wallet's "Pending invoices" tab. On
success the caller refetches the invoice list (and balance) so the change renders.

Note it lives under `/invoice` (like `/invoice/list` + `/invoice/deposit`), NOT under
`/wallet` — so it targets `window.config.userApiUrl` directly, not the `/wallet`-suffixed
`apiUrl`. An unknown / already-settled `invoice_id` currently comes back as a Prisma
`findUniqueOrThrow` 500 rather than a clean 404 (probed live 4 Jul 2026). See
`docs/api.owners-hub.wallet.md`. */

/** Cancel a pending invoice. Returns the `invoice_id` for the caller to refetch around;
   throws (rejected) on an unknown / already-settled invoice. */
export const cancelInvoice = createAsyncThunk<{ invoice_id: string }, { invoice_id: string }, GenericError>(
  'wallet/cancelInvoice',
  async ({ invoice_id }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      await axios.post(`${window.config.userApiUrl}/invoice/cancel`, { invoice_id }, { headers });
      DEBUG && console.log('[Wallet Slice] cancelInvoice cancelled', invoice_id);
      return { invoice_id };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

const initialState: WalletState = {
  main: 0,
  acquisition: 0,
  total: 0,
  currency: getCurrentCurrency(),
  credits: null,
  creditsReadFailed: false,

  wallets: [],
  balance: 0,

  operations: [],
  operationsTotal: 0,

  transactions: [],
  transactionsTotal: 0,

  loading: false,
  loaded: false,
  error: null,

  balanceLoading: false,
  balanceLoaded: false,
  balanceError: null,

  operationsLoading: false,
  operationsLoaded: false,
  operationsError: null,

  transactionsLoading: false,
  transactionsLoaded: false,
  transactionsError: null,

  pendingWithdrawals: [],
  pendingWithdrawalsLoading: false,
  pendingWithdrawalsLoaded: false,
  pendingWithdrawalsError: null,

  resolvedWithdrawals: [],
  resolvedWithdrawalsLoading: false,
  resolvedWithdrawalsLoaded: false,
  resolvedWithdrawalsError: null,

  withdrawalSubmitting: false,
  withdrawalSubmitError: null,

  invoices: [],
  invoicesTotal: 0,
  invoicesLoading: false,
  invoicesLoaded: false,
  invoicesError: null,

  payingInvoiceId: null,
  payInvoiceError: null,

  cancellingInvoiceId: null,
  cancelInvoiceError: null,
};

export const ownerWalletSlice = createSlice({
  name: 'ownerWalletSlice',
  initialState,
  reducers: {
    /** Clear a previous create-withdrawal error (e.g. when the payout dialog reopens). */
    clearWithdrawalSubmitError(state) {
      state.withdrawalSubmitError = null;
    },
    /**
     * Wipe ALL wallet state back to empty. Every figure here (balances, operations,
     * transactions, withdrawal requests, invoices) is OWNER-scoped — resolved
     * server-side from the access token — so it must not outlive the session it was
     * fetched for. Dispatched by BOTH session-teardown chokepoints in `store/index.ts`:
     * `logout` (so a shared browser never shows the previous owner's balances) and an
     * identity change (a support agent switching the impersonated owner via a magic
     * link without signing out). Mirrors `resetUserPreferences` / `resetOwnerHubStats`
     * / `resetCasinoInstance`. `currency` re-seeds from the same localStorage fast-path
     * the slice initialised with (it's a display preference, not owner data).
     */
    resetWallet: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /* ----- /wallet/list ----- */
      .addCase(fetchOwnerWallets.pending, (state) => {
        DEBUG && console.log('[Wallet Slice] fetchOwnerWallets list pending');

        state.loading = true;
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchOwnerWallets.fulfilled, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchOwnerWallets list fulfilled', { payload: action.payload });

        state.loading = false;
        state.loaded = true;

        const wallets = Array.isArray(action.payload?.wallets?.data) ? action.payload.wallets.data : [];
        state.wallets = wallets;

        const main = wallets.find((w) => w.wallet_type === 'MAIN');
        const acquisitionWallets = wallets.filter((w) => w.wallet_type === 'ACQUISITION');
        // Owner-level virtual-currency AI-credits wallet (currency AIC). Balance is a credit COUNT (not
        // money), so it stays OUT of `total`/`currency`. The BE only creates this wallet on the first
        // purchase, but the loaded list is authoritative: an absent row means a real 0 balance (agrees
        // with GET /wallet/balance/ai-credits), so show 0 rather than hiding the chip. `credits` is null
        // ONLY before the list first loads.
        const creditsWallet = wallets.find((w) => w.wallet_type === 'AI_CREDITS');

        state.main = toNumber(main?.balance);
        // Sum the per-brand acquisition wallets (was only the first match via `find`).
        state.acquisition = acquisitionWallets.reduce((sum, w) => sum + toNumber(w.balance), 0);
        state.total = state.main + state.acquisition;
        state.credits = creditsWallet ? toNumber(creditsWallet.balance) : 0;
        state.creditsReadFailed = false;
        state.currency = main?.currency ?? acquisitionWallets[0]?.currency ?? state.currency;

        DEBUG &&
          console.log('[Wallet Slice] list fulfilled', { wallets, main: state.main, acquisition: state.acquisition });
      })
      .addCase(fetchOwnerWallets.rejected, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchOwnerWallets list rejected', { payload: action.payload });
        state.loading = false;
        state.loaded = true;
        state.error = action.payload?.error;
        state.creditsReadFailed = true;
      })

      /* ----- /wallet/balance ----- */
      .addCase(fetchWalletBalance.pending, (state) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletBalance pending');
        state.balanceLoading = true;
        state.balanceLoaded = false;
        state.balanceError = null;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletBalance fulfilled', { payload: action.payload });
        state.balanceLoading = false;
        state.balanceLoaded = true;
        state.balance = toNumber(action.payload?.balance);
        state.currency = action.payload?.currency ?? state.currency;
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletBalance rejected', { payload: action.payload });
        state.balanceLoading = false;
        state.balanceLoaded = true;
        state.balanceError = action.payload?.error;
      })

      /* ----- /wallet/operations ----- */
      .addCase(fetchWalletOperations.pending, (state) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletOperations pending');
        state.operationsLoading = true;
        state.operationsLoaded = false;
        state.operationsError = null;
      })
      .addCase(fetchWalletOperations.fulfilled, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletOperations fulfilled', { payload: action.payload });
        state.operationsLoading = false;
        state.operationsLoaded = true;
        state.operations = Array.isArray(action.payload?.data) ? action.payload.data : [];
        state.operationsTotal = action.payload?.total ?? state.operations.length;
      })
      .addCase(fetchWalletOperations.rejected, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletOperations rejected', { payload: action.payload });
        state.operationsLoading = false;
        state.operationsLoaded = true;
        state.operationsError = action.payload?.error;
      })

      /* ----- /wallet/transactions ----- */
      .addCase(fetchWalletTransactions.pending, (state) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletTransactions pending');
        state.transactionsLoading = true;
        state.transactionsLoaded = false;
        state.transactionsError = null;
      })
      .addCase(fetchWalletTransactions.fulfilled, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletTransactions fulfilled', { payload: action.payload });
        state.transactionsLoading = false;
        state.transactionsLoaded = true;
        state.transactions = Array.isArray(action.payload?.data) ? action.payload.data : [];
        state.transactionsTotal = action.payload?.total ?? state.transactions.length;
      })
      .addCase(fetchWalletTransactions.rejected, (state, action) => {
        DEBUG && console.log('[Wallet Slice] fetchWalletTransactions rejected', { payload: action.payload });
        state.transactionsLoading = false;
        state.transactionsLoaded = true;
        state.transactionsError = action.payload?.error;
      })

      /* ----- /wallet/withdrawal-requests (pending) ----- */
      .addCase(fetchPendingWithdrawalRequests.pending, (state) => {
        state.pendingWithdrawalsLoading = true;
        state.pendingWithdrawalsLoaded = false;
        state.pendingWithdrawalsError = null;
      })
      .addCase(fetchPendingWithdrawalRequests.fulfilled, (state, action) => {
        state.pendingWithdrawalsLoading = false;
        state.pendingWithdrawalsLoaded = true;
        state.pendingWithdrawals = Array.isArray(action.payload?.data) ? action.payload.data : [];
      })
      .addCase(fetchPendingWithdrawalRequests.rejected, (state, action) => {
        state.pendingWithdrawalsLoading = false;
        state.pendingWithdrawalsLoaded = true;
        state.pendingWithdrawalsError = action.payload?.error;
      })

      /* ----- /wallet/withdrawal-requests (resolved) ----- */
      .addCase(fetchResolvedWithdrawalRequests.pending, (state) => {
        state.resolvedWithdrawalsLoading = true;
        state.resolvedWithdrawalsLoaded = false;
        state.resolvedWithdrawalsError = null;
      })
      .addCase(fetchResolvedWithdrawalRequests.fulfilled, (state, action) => {
        state.resolvedWithdrawalsLoading = false;
        state.resolvedWithdrawalsLoaded = true;
        state.resolvedWithdrawals = Array.isArray(action.payload?.data) ? action.payload.data : [];
      })
      .addCase(fetchResolvedWithdrawalRequests.rejected, (state, action) => {
        state.resolvedWithdrawalsLoading = false;
        state.resolvedWithdrawalsLoaded = true;
        state.resolvedWithdrawalsError = action.payload?.error;
      })

      /* ----- POST /wallet/withdrawal-request (create) ----- */
      .addCase(createWithdrawalRequest.pending, (state) => {
        state.withdrawalSubmitting = true;
        state.withdrawalSubmitError = null;
      })
      .addCase(createWithdrawalRequest.fulfilled, (state) => {
        state.withdrawalSubmitting = false;
      })
      .addCase(createWithdrawalRequest.rejected, (state, action) => {
        state.withdrawalSubmitting = false;
        state.withdrawalSubmitError = action.payload?.error;
      })

      /* ----- POST {userApiUrl}/invoice/list ----- */
      .addCase(fetchInvoices.pending, (state) => {
        state.invoicesLoading = true;
        state.invoicesLoaded = false;
        state.invoicesError = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesLoaded = true;
        state.invoices = Array.isArray(action.payload?.data) ? action.payload.data : [];
        state.invoicesTotal = action.payload?.total ?? state.invoices.length;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesLoaded = true;
        state.invoicesError = action.payload?.error;
      })

      /* ----- POST /wallet/pay-invoice ----- */
      .addCase(payInvoiceFromWallet.pending, (state, action) => {
        state.payingInvoiceId = action.meta.arg.invoice_id;
        state.payInvoiceError = null;
      })
      .addCase(payInvoiceFromWallet.fulfilled, (state) => {
        state.payingInvoiceId = null;
      })
      .addCase(payInvoiceFromWallet.rejected, (state, action) => {
        state.payingInvoiceId = null;
        state.payInvoiceError = action.payload?.error;
      })

      /* ----- POST /invoice/cancel ----- */
      .addCase(cancelInvoice.pending, (state, action) => {
        state.cancellingInvoiceId = action.meta.arg.invoice_id;
        state.cancelInvoiceError = null;
      })
      .addCase(cancelInvoice.fulfilled, (state) => {
        state.cancellingInvoiceId = null;
      })
      .addCase(cancelInvoice.rejected, (state, action) => {
        state.cancellingInvoiceId = null;
        state.cancelInvoiceError = action.payload?.error;
      });
  },
});

export const { clearWithdrawalSubmitError, resetWallet } = ownerWalletSlice.actions;

export default ownerWalletSlice.reducer;
